<template>
  <shared-product-provide
    v-if="account && Object.keys(account).length > 0"
    :product="product"
    :fromModule="fromModule"
    :toModule="toModule"
  >
    <div class="c-box" v-if="!loading">
      <loader :loading="sending" :label="getCountVariations()"/>

      <header class="c-box__heading" :class="{disabledHeader: product.status === 'cannotSend'}" @click="toggleProductInfo()">
        <span v-once>{{ product.title }}</span>
        <i :class="toggleIconClass()" class="u-floatRight icon"></i>
        <span slot="header-right">
          <span v-if="isProductStatusNotSending" class="badge u-floatRight status" :class="product.status">{{ productStatusLabel }}</span>
          <slot name="header-right"></slot>
        </span>
      </header>

      <div class="c-box__body" v-show="show && !everyVariationWithErrorOrSent">
        <div class="c-box__inner">
          <panel v-if="product" title="Informações gerais do produto">
            <div slot="header-right">
              <button :disabled="addingImage" :class="{ 'is-loading': addingImage }" @click="addingImage = true" slot="header-actions" class="hint hint--left btn btn--info" data-label="Adicionar uma imagem em uma ou mais variações">
                Adicionar Imagem
              </button>

              <modal-add-image :visible="addingImage" :variations="product.variations" :callback="uploadedImage" @close="close => addingImage = close" :module="moduleUploadImageModal"/>
            </div>

            <div class="l-row">
              <form-group label="Título" :columns="12" slot="product" :error="getError('title', product.productId)" :required="true">
                <input class="form-input" type="text" v-model="product.title" :disabled="disabledInput('title')">
                <small v-if="maxTitleLengthDI !== null" class="form-help" :class="titleLength">{{ getTitleLengthLabel() }}</small>
              </form-group>

              <form-group label="Descrição" :columns="12" slot="product" :error="getError('description', product.productId)" :required="true">
                <quill-editor v-if="allowDescriptionHtml" :options="{ placeholder: 'Adicione a descrição do seu produto aqui' }" v-model="product.description" :disabled="disabledInput('description')"/>
                <textarea v-else class="form-textarea" rows="8" :options="{ placeholder: 'Adicione a descrição do seu produto aqui' }" v-model="product.description" :disabled="disabledInput('description')"></textarea>
                <small v-if="maxDescriptionLengthDI !== null" class="form-help" :class="descriptionLength">{{ getDescriptionLengthLabel() }}</small>
              </form-group>
              <slot/>
            </div>

            <div class="l-row">
              <form-group label="Marca" :columns="3" slot="product" :error="getError('brand', product.productId)" :required="true">
                <input type="string" class="form-input" v-model="product.brand">
              </form-group>

              <form-group :label="$t('ncm.label')" :error="getError('ncm', product.productId)" :required="true">
                <input type="number" class="form-input" v-model="product.ncm">
              </form-group>

              <form-group :label="$t('crossDocking.label')" :error="getError('crossDocking', product.productId)">
                <input type="number" class="form-input" v-model="crossDocking">
                <small class="form-help"> (Em dias)</small>
              </form-group>
            </div>
          </panel>

          <panel title="Atributos personalizados (opcional)">
            <div slot="header-right">
              <button class="btn btn--info" :disabled="disabledInputs()" @click.prevent="addAttributeRow">
                Adicionar linha
              </button>
            </div>

            <div class="l-row" v-if="!customAttributes.length">
              <div class="l-col-12@md">
                <small class="form-help">Sem atributos. Clique em “Adicionar linha”.</small>
              </div>
            </div>

            <div class="l-row attr-row" v-for="(attr, idx) in customAttributes" :key="`attr-${idx}`">
              <form-group :label="'Chave'" :columns="5" :error="getError(`customAttributes[${idx}].key`, product.productId)" :required="true">
                <input
                  class="form-input"
                  type="text"
                  :disabled="disabledInputs()"
                  v-model.trim="attr.key"
                  placeholder="Ex.: Material">
              </form-group>

              <form-group :label="'Valor'" :columns="5" :error="getError(`customAttributes[${idx}].value`, product.productId)" :required="true">
                <input
                  class="form-input"
                  type="text"
                  :disabled="disabledInputs()"
                  v-model.trim="attr.value"
                  placeholder="Ex.: Algodão">
              </form-group>

              <div class="l-col-2@md attr-actions">
                <button
                  class="btn btn--danger"
                  :disabled="disabledInputs()"
                  @click.prevent="removeAttributeRow(idx)">
                  Remover
                </button>
              </div>
            </div>
          </panel>

          <div class="divider">
            <span>Informações das variações do produto</span>
          </div>

          <div class="l-row vertical-flex">
            <div class="l-col-12@md" v-for="variation in product.variations" :key="variation">
              <component :is="marketplaceVariationProvide" ref="product.variations" :fromModule="fromModule" :toModule="toModule" :variation="variation" :product-id="product.productId" :account="{ identifier: account.identifier }"/>
            </div>
          </div>
        </div>
      </div>

    </div>
  </shared-product-provide>
</template>

<script>
  import Panel from '@/components/template/Panel'
  import { quillEditor } from 'vue-quill-editor'
  import ModalAddImage from '@/components/marketplace/shared/provide/ModalAddImage'
  import {mapActions, mapGetters} from 'vuex'
  import FormGroup from '@/components/template/FormGroup'
  import Multiselect from 'vue-multiselect'
  import Loader from '@/components/template/Loader'
  import { getCharacteristicLabel, getProductCharacteristics } from '@/components/marketplace/shared/provide/presetCharacteristics'
  import MarketplaceProvideVariationMagalu from '@/components/marketplace/magalu/provide/Variation'

  export default {
    inject: ['maxDescriptionLengthDI', 'moduleDI', 'allowDescriptionHtml', 'maxTitleLengthDI'],
    provide: {
      moduleDI: {
        type: 'marketplace',
        identifier: 'magalu'
      },
      allowDescriptionHtml: true
    },
    data: () => ({
      loading: false,
      maxTitleLengthDI: 60,
      maxDescriptionLengthDI: 2000,
      show: false,
      isProductStatusNotSending: false,
      productStatusLabel: '',
      addingImage: false,

      ncm: null,
      crossDocking: 0,

      customAttributes: []
    }),
    props: {
      account: {
        type: Object,
        required: true
      },
      product: {
        type: Object,
        required: true
      },
      fromModule: {
        type: Object,
        required: true
      },
      toModule: {
        type: Object,
        required: true
      },
      loading: {
        type: Boolean,
        default: false
      },
      disabledInputList: {
        type: Array,
        default: () => []
      }
    },
    async mounted () {
      if (!this.allowDescriptionHtml) {
        this.removeHtml()
      }

      if (Array.isArray(this.product.customAttributes) && this.product.customAttributes.length) {
        this.customAttributes = this.product.customAttributes.map(a => ({
          key: (a.key || '').toString(),
          value: (a.value || '').toString()
        }))
      }

      const characteristics = getProductCharacteristics(this.product)

      this.customAttributes.forEach(attribute => {
        if (!attribute.value) {
          attribute.value = getCharacteristicLabel(attribute.key, characteristics) || ''
        }
      })

      this.loading = true
      this.loading = false
      this.$parent.loadingProducts(false)
    },
    methods: {
      ...mapActions('errors', [
        'addError',
        'clearErrors'
      ]),
      addAttributeRow () {
        this.customAttributes.push({ key: '', value: '' })
      },
      removeAttributeRow (idx) {
        this.customAttributes.splice(idx, 1)
      },
      attrIdFromKey (key) {
        return key
          ? key.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')
          : ''
      },
      validateCustomAttributesAndPushErrors () {
        let ok = true
        const pid = this.product.productId
        this.customAttributes.forEach((a, idx) => {
          const hasKey = !!(a.key && a.key.toString().trim())
          const hasVal = !!(a.value && a.value.toString().trim())
          if (hasVal && !hasKey) {
            this.addError({ productId: pid, property: `customAttributes[${idx}].key`, error: { message: 'required', value: 1 } })
            ok = false
          }
          if (hasKey && !hasVal) {
            this.addError({ productId: pid, property: `customAttributes[${idx}].value`, error: { message: 'required', value: 1 } })
            ok = false
          }
        })
        return ok
      },
      validateVariationCustomAttributesAndPushErrors (variation) {
        const pid = this.product.productId
        const sku = variation.sku || variation.id
        let ok = true

        const list = Array.isArray(variation.customAttributes) ? variation.customAttributes : []
        list.forEach((a, idx) => {
          const hasKey = !!(a.key && a.key.toString().trim())
          const hasVal = !!(a.value && a.value.toString().trim())

          if (hasVal && !hasKey) {
            this.addError({
              productId: pid,
              sku,
              property: `customAttributes[${idx}].key`,
              error: { message: 'required', value: 1 }
            })
            ok = false
          }
          if (hasKey && !hasVal) {
            this.addError({
              productId: pid,
              sku,
              property: `customAttributes[${idx}].value`,
              error: { message: 'required', value: 1 }
            })
            ok = false
          }
        })

        return ok
      },
      formatProduct () {
        this.clearErrors()
        const validProdAttrs = this.validateCustomAttributesAndPushErrors()
        if (!validProdAttrs) return []

        const selected = (this.product.variations || []).filter(v => v && v.selected)
        let validAllVars = true
        selected.forEach(v => {
          const ok = this.validateVariationCustomAttributesAndPushErrors(v)
          if (!ok) validAllVars = false
        })
        if (!validAllVars) return []

        if (this.product.brand === '' || this.product.brand === undefined || this.product.brand === null) {
          this.addError({
            productId: this.product.productId,
            property: 'brand',
            error: {
              message: 'required',
              value: 1
            }
          })
        }

        if (this.product.ncm === '' || this.product.ncm === undefined || this.product.ncm === null) {
          this.addError({
            productId: this.product.productId,
            property: 'ncm',
            error: {
              message: 'required',
              value: 1
            }
          })
        }

        if (!this.product.description || this.product.description.trim().length < 50) {
          this.addError({
            productId: this.product.productId,
            property: 'description',
            error: {
              message: 'minLength',
              value: 50
            }
          })
        }

        const customAttrs = (this.customAttributes || [])
          .filter(a => (a.key || '').trim() && (a.value || '').trim())
          .map(a => {
            const id = this.attrIdFromKey(a.key)
            const value = a.value.toString()
            return {
              id: id || 'CUSTOM',
              label: a.key.toString(),
              valueId: value,
              valueLabel: value
            }
          })

        // 4) monta o payload por variação (agora acrescentando os custom da VARIAÇÃO)
        return selected.map(variation => {
          const hasColorAttribute = variation.attributes.color !== undefined && variation.attributes.color !== null && variation.attributes.color !== ''
          const hasSizeAttribute = variation.attributes.size !== undefined && variation.attributes.size !== null && variation.attributes.size !== ''
          const hasVoltageAttribute = variation.attributes.voltage !== undefined && variation.attributes.voltage !== null && variation.attributes.voltage !== ''

          const attributes = []

          if (hasColorAttribute) {
            attributes.push({
              id: 'COLOR',
              label: 'Cor',
              valueId: variation.attributes.color,
              valueLabel: variation.attributes.color
            })
          }

          if (hasSizeAttribute) {
            attributes.push({
              id: 'SIZE',
              label: 'Tamanho',
              valueId: variation.attributes.size,
              valueLabel: variation.attributes.size
            })
          }

          if (hasVoltageAttribute) {
            attributes.push({
              id: 'VOLTAGE',
              label: 'Voltagem',
              valueId: variation.attributes.voltage,
              valueLabel: variation.attributes.voltage
            })
          }

          // 4.a) atributos personalizados da VARIAÇÃO (NOVO)
          const varCustomAttrs = (variation.customAttributes || [])
            .filter(a => (a.key || '').trim() && (a.value || '').trim())
            .map(a => {
              const id = this.attrIdFromKey(a.key)
              const value = a.value.toString()
              return {
                id: id || 'CUSTOM',
                label: a.key.toString(),
                valueId: value,
                valueLabel: value
              }
            })

          // 4.b) merge final (cor/tamanho/voltagem) + (custom do produto) + (custom da variação)
          const mergedAttributes = attributes.concat(customAttrs, varCustomAttrs)

          return {
            account: { identifier: this.account.identifier },
            productId: this.product.productId,
            title: this.product.title,
            sku: variation.sku,
            description: this.product.description,
            price: variation.price,
            stock: variation.stock,
            ean: variation.ean,
            weight: variation.weight,
            width: variation.width,
            height: variation.height,
            length: variation.length,
            ncm: this.product.ncm,
            brand: this.product.brand,
            crossDocking: { quantity: this.crossDocking },
            images: variation.images.length ? variation.images : null,
            source: this.fromModule,
            attributes: mergedAttributes,
            original: { product: this.product, variation }
          }
        })
      },
      disabledInputs () {
        return this.product.status === 'sent' || this.product.status === 'error'
      },
      removeHtml () {
        const doc = new DOMParser().parseFromString(this.product.description, 'text/html')
        let text = doc.body.innerText || doc.body.textContent || ''

        text = text
          .replace(/\r\n/g, '\n')
          .replace(/\r/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim()

        this.product.description = text
      },
      getDescriptionLengthLabel () {
        const length = this.product.description ? this.product.description.length : 0
        const minLength = 50
        if (length < minLength) {
          return `${length}/${this.maxDescriptionLengthDI} (mínimo: ${minLength})`
        }
        return `${length}/${this.maxDescriptionLengthDI}`
      },
      getTitleLengthLabel () {
        return `${this.product.title.length}/${this.maxTitleLengthDI}`
      },
      getCountVariations () {
        const selectedVariations = this.product.variations
          .filter(variation => variation.selected)

        const sentVariations = selectedVariations
          .filter(variation => variation.status !== 'sending')
          .length

        return `${sentVariations}/${selectedVariations.length} variações enviadas`
      },
      uploadedImage (urls, variations) {
        for (let i in variations) {
          const sku = variations[i].sku
          const variation = this.product.variations.find(variation => variation.sku === sku)

          if (variation === undefined) {
            continue
          }

          for (let j in urls) {
            variation.images.push(urls[j])
          }
        }

        variations.splice(0, variations.length)
      },
      disabledInput (input) {
        if (!this.disabledInputList.length) {
          return false
        }

        return this.disabledInputList.indexOf(input) !== -1
      },
      toggleProductInfo () {
        if (this.product.status === 'sent' || this.product.status === 'error' || this.product.status === 'cannotSend') {
          return false
        }

        this.show = !this.show
      },
      toggleIconClass () {
        return {
          'icon-circle-up': this.show,
          'icon-circle-down': !this.show || this.everyVariationWithErrorOrSent
        }
      }
    },
    computed: {
      ...mapGetters('errors', [
        'getError'
      ]),
      ...mapGetters('module', [
        'getDefinitionModuleByIdentifierAndType'
      ]),
      moduleDefinition () {
        return this.getDefinitionModuleByIdentifierAndType('marketplace', 'magalu')
      },
      sending () {
        return !!this.product.variations
          .filter(variation => variation.status === 'sending').length
      },
      marketplaceVariationProvide () {
        return `MarketplaceProvideVariationMagalu`
      },
      everyVariationWithErrorOrSent () {
        return this.product.variations.every(variation => variation.status === 'error' || variation.status === 'sent')
      },
      descriptionLength () {
        const length = this.product.description ? this.product.description.length : 0
        return {
          't-success': length >= 50 && length <= this.maxDescriptionLengthDI,
          't-error': length < 50 || length > this.maxDescriptionLengthDI
        }
      },
      titleLength () {
        return {
          't-success': this.product.title.length <= this.maxTitleLengthDI,
          't-error': this.product.title.length > this.maxTitleLengthDI
        }
      },
      moduleUploadImageModal () {
        return {
          type: this.toModule.type,
          identifier: this.toModule.identifier
        }
      }
    },
    watch: {},
    components: {
      FormGroup,
      Multiselect,
      Loader,
      Panel,
      quillEditor,
      ModalAddImage,
      MarketplaceProvideVariationMagalu
    }
  }
</script>

<style lang="sass" scoped>
  .c-box
    margin-bottom: 16px

  .c-box__body
    background: #d9edf7

  .c-box__heading
    cursor: pointer
    height: 50px
    line-height: 2
    font-size: 15px
    color: #31708f
    background-color: #d9edf7
    border-color: #bde2fb

  .icon-circle-down, .icon-circle-up
    font-size: 30px

  .c-box__body
    border-color: #bde2fb

  .divider
    margin-bottom: 16px

  .status
    margin-right: 20px
    margin-top: 4px

  .disabledHeader
    cursor: not-allowed
    background-color:  #E7E7E7
    border-color: #D3D3D3

    .icon
      opacity: 0.3

  .attr-row
    align-items: center

  .attr-actions
    display: flex
    align-items: flex-start

  .attr-actions .btn
    margin-top: 8px
</style>



// WEBPACK FOOTER //
// Product.vue?5ecc71da